/* Números con unidad, en la pantalla · CATÁLOGO VIVO F2.
   Espejo de `backend/app/catalogo_vivo/unidades.py`: lo mismo, para validar
   mientras se escribe («10k» → 10 000 Ω) sin ir al servidor en cada tecla.
   El servidor vuelve a validar al guardar y manda él. Se prueban con la
   misma tabla de casos. */

const PREFIJOS = { p: 1e-12, n: 1e-9, u: 1e-6, 'µ': 1e-6, 'μ': 1e-6, m: 1e-3, k: 1e3, K: 1e3, M: 1e6, G: 1e9 };
const BASES_SI = new Set(['Ω', 'V', 'A', 'W', 'F', 'H', 'Hz', 'g', 's']);
const ALIAS = { ohm: 'Ω', ohms: 'Ω', ohmios: 'Ω', ohmio: 'Ω', r: 'Ω', R: 'Ω', hz: 'Hz', HZ: 'Hz' };

const NUM = '[0-9]+(?:[.,][0-9]+)?';
const FRACCION = new RegExp(`^\\s*(${NUM})\\s*/\\s*(${NUM})\\s*(.*)$`);
const SIMPLE = new RegExp(`^\\s*([-+]?${NUM})\\s*(.*)$`);
const RKM = /^\s*([0-9]+)([pnuµμmkKMGR])([0-9]+)\s*(.*)$/;

export class UnidadNoValida extends Error {}

const numero = (t) => {
  const n = Number(String(t).replace(',', '.'));
  if (Number.isNaN(n)) throw new UnidadNoValida(`«${t}» no es un número.`);
  return n;
};

function factorDe(sufijo, base = '', unidades = []) {
  const s = (sufijo || '').trim();
  if (!s) return 1;
  if (base && (s === base || ALIAS[s] === base)) return 1;
  const declarada = (unidades || []).find((u) => u.simbolo === s);
  if (declarada) return Number(declarada.factor ?? 1);
  if (BASES_SI.has(base)) {
    let resto = s;
    for (const b of [base, ...Object.keys(ALIAS).filter((a) => ALIAS[a] === base)]) {
      if (resto.endsWith(b) && resto.length > b.length) { resto = resto.slice(0, -b.length).trim(); break; }
    }
    if (resto in PREFIJOS) return PREFIJOS[resto];
  }
  const permitidas = [base, ...(unidades || []).map((u) => u.simbolo)].filter(Boolean);
  if (permitidas.length) throw new UnidadNoValida(`«${s}» no es una unidad de este dato. Usa ${permitidas.join(', ')}.`);
  throw new UnidadNoValida('Este dato es un número sin unidad. Escribe solo el número.');
}

// Redondeo para comparar y mostrar sin colas de coma flotante (4.7e-6 * 1 = 0.0000047000000000000004).
const limpio = (n) => Number(n.toPrecision(12));

export function interpretar(texto, base = '', unidades = []) {
  if (texto == null || !String(texto).trim()) throw new UnidadNoValida('Escribe un número.');
  const t = String(texto).split(/\s+/).filter(Boolean).join(' ');
  let m = RKM.exec(t);
  if (m) {
    const [, entero, prefijo, decimales, sufijo] = m;
    const valor = numero(`${entero}.${decimales}`);
    if (prefijo === 'R') {
      if (base !== 'Ω') throw new UnidadNoValida('La «R» como punto decimal solo vale para resistencias (Ω).');
      return limpio(valor);
    }
    return limpio(valor * factorDe(prefijo + sufijo, base, unidades));
  }
  m = FRACCION.exec(t);
  if (m) {
    const divisor = numero(m[2]);
    if (divisor === 0) throw new UnidadNoValida('Una fracción no puede dividir entre cero.');
    return limpio((numero(m[1]) / divisor) * factorDe(m[3], base, unidades));
  }
  m = SIMPLE.exec(t);
  if (!m) throw new UnidadNoValida(`«${t}» no es un número. Escribe por ejemplo 10, 4.7 o 1/4.`);
  return limpio(numero(m[1]) * factorDe(m[2], base, unidades));
}

const ESCALAS = [[1e9, 'G'], [1e6, 'M'], [1e3, 'k'], [1, ''], [1e-3, 'm'], [1e-6, 'µ'], [1e-9, 'n'], [1e-12, 'p']];
const corto = (n) => String(limpio(n));

export function formatear(valor, base = '') {
  if (valor == null || valor === '') return '';
  const v = Number(valor);
  if (BASES_SI.has(base) && v !== 0) {
    const abs = Math.abs(v);
    for (const [escala, prefijo] of ESCALAS) {
      if (abs >= escala * (1 - 1e-12)) {
        if (escala < 1 && abs >= 0.1 && ['W', 'A', 'V', 'g', 's'].includes(base)) break;
        return `${corto(v / escala)} ${prefijo}${base}`.trim();
      }
    }
  }
  return `${corto(v)} ${base || ''}`.trim();
}
